import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { FeedbackType } from '@malash-office/types';
import { captureScreenshot } from './captureScreenshot';
import { moduleForPath } from './feedbackModule';
import NewFeedbackDialog from './newFeedback/NewFeedbackDialog';
import type { NewFeedbackSeed } from './newFeedback/useNewFeedbackForm';

interface FeedbackContextValue {
  /** Opens "פנייה חדשה" over the current page, with `type` picked (a "מה תרצה לשתף?" tile). */
  openNewFeedback: (type?: FeedbackType) => void;
  /** True while the page is being photographed, just before the form opens. */
  capturing: boolean;
}

const FeedbackContext = createContext<FeedbackContextValue>({ openNewFeedback: () => undefined, capturing: false });

export const useFeedbackLauncher = () => useContext(FeedbackContext);

/**
 * Where "פנייה חדשה" lives for the whole dashboard. Since 2026-10-29 it is opened only from
 * הגדרות → פניות ומשוב — the floating "דיווח ומשוב" button on every screen was removed, as asked.
 *
 * The page is photographed first and the form opened after, so the form is not in the picture.
 * The link and the module are the page the user was on when they asked (`moduleForPath`).
 */
export const FeedbackProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [seed, setSeed] = useState<NewFeedbackSeed | null>(null);
  const [capturing, setCapturing] = useState(false);
  const busy = useRef(false);

  const openNewFeedback = useCallback(
    async (type: FeedbackType = 'bug') => {
      if (busy.current) return;
      busy.current = true;
      setCapturing(true);
      const screenshot = await captureScreenshot();
      setCapturing(false);
      busy.current = false;
      setSeed({ type, link: window.location.href, module: moduleForPath(location.pathname), screenshot });
    },
    [location.pathname]
  );

  const value = useMemo(() => ({ openNewFeedback, capturing }), [openNewFeedback, capturing]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      {seed && <NewFeedbackDialog seed={seed} onClose={() => setSeed(null)} />}
    </FeedbackContext.Provider>
  );
};
